import React, { useState, useEffect, useCallback } from 'react';
import { MapPinIcon, PhoneIcon, ClockIcon, ExclamationTriangleIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline';
import BaseMap, { MARKER_COLORS, MARKER_TYPES } from './BaseMap';
import geolocationService from '../../services/geolocationService';
import { websocketService } from '../../services/emergencyWebsocket';

const PatientTrackingMap = ({ 
  requestId, 
  driverLocation,
  driverInfo,
  onLocationUpdate,
  initialPatientLocation,
  className = '',
  height = '400px',
  showControls = true,
  autoCenter = true
}) => {
  const [patientLocation, setPatientLocation] = useState(null);
  const [isLocationSharing, setIsLocationSharing] = useState(false);
  const [locationPermission, setLocationPermission] = useState('unknown');
  const [distanceInfo, setDistanceInfo] = useState(null);
  const [locationSubscription, setLocationSubscription] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [estimatedArrival, setEstimatedArrival] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [shouldRecenter, setShouldRecenter] = useState(false);

  // Request location permission
  const requestLocationPermission = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const permission = await geolocationService.checkPermission();
      setLocationPermission(permission);
      
      if (permission === 'granted') {
        const position = await geolocationService.getCurrentPosition();
        const formattedPosition = geolocationService.formatPosition(position);
        setPatientLocation(formattedPosition);
        
        // Send location permission grant to server
        websocketService.send('location:grant-permission', { requestId });
        
        return true;
      } else if (permission === 'prompt') {
        // Try to request permission
        const position = await geolocationService.requestPermission();
        setPatientLocation(position);
        setLocationPermission('granted');
        
        // Send location permission grant to server
        websocketService.send('location:grant-permission', { requestId });
        
        return true;
      } else {
        throw new Error('Location permission denied');
      }
    } catch (error) {
      console.error('Failed to get location permission:', error);
      setError(error.userMessage || error.message);
      setLocationPermission('denied');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [requestId]);

  // Start location sharing
  const startLocationSharing = useCallback(async () => {
    if (!requestId) {
      setError('No request ID provided');
      return;
    }

    try {
      const hasPermission = await requestLocationPermission();
      if (!hasPermission) return;

      // Start watching location changes
      const subscriptionId = geolocationService.startWatching(
        (position) => {
          // #region agent log
          fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'PatientTrackingMap.js:83',message:'Patient location received from geolocation service',data:{hasPosition:!!position,latitude:position?.latitude,longitude:position?.longitude,accuracy:position?.accuracy,requestId},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'O'})}).catch(()=>{});
          // #endregion agent log
          
          setPatientLocation(position);
          
          // Send location update to server
          const locationUpdate = {
            requestId,
            latitude: position.latitude,
            longitude: position.longitude,
            accuracy: position.accuracy
          };
          
          // #region agent log
          fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'PatientTrackingMap.js:95',message:'Sending patient location update via WebSocket',data:{locationUpdate,requestId},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'O'})}).catch(()=>{});
          // #endregion agent log
          
          websocketService.send('location:update', locationUpdate);

          // Notify parent component
          if (onLocationUpdate) {
            onLocationUpdate(position);
          }
        },
        (error) => {
          console.error('Location watching error:', error);
          // Use user-friendly message if available, otherwise use technical message
          setError(error.userMessage || error.message);
        }
      );

      setLocationSubscription(subscriptionId);
      setIsLocationSharing(true);
      console.log('Started location sharing for patient');
      
    } catch (error) {
      console.error('Failed to start location sharing:', error);
      setError(error.userMessage || error.message);
    }
  }, [requestId, onLocationUpdate, requestLocationPermission]);

  // Stop location sharing
  const stopLocationSharing = useCallback(() => {
    if (locationSubscription) {
      geolocationService.stopWatching(locationSubscription);
      setLocationSubscription(null);
    }
    
    setIsLocationSharing(false);
    
    // Notify server
    if (requestId) {
      websocketService.send('location:stop-sharing', { requestId });
    }
    
    console.log('Stopped location sharing for patient');
  }, [locationSubscription, requestId]);

  // Calculate estimated arrival time
  const calculateEstimatedArrival = useCallback(() => {
    if (distanceInfo && distanceInfo.eta) {
      const now = new Date();
      const arrivalTime = new Date(now.getTime() + (distanceInfo.eta * 60 * 1000));
      setEstimatedArrival(arrivalTime);
    } else {
      setEstimatedArrival(null);
    }
  }, [distanceInfo]);

  // Request distance info
  const requestDistanceInfo = useCallback(() => {
    if (requestId) {
      websocketService.send('location:get-distance', { requestId });
    }
  }, [requestId]);

  // Recenter map to show both patient and driver locations
  const recenterMap = useCallback(() => {
    const patientLat = patientLocation?.latitude || patientLocation?.lat;
    const patientLng = patientLocation?.longitude || patientLocation?.lng;
    
    if (patientLocation && driverLocation) {
      // Show both locations by triggering auto-zoom
      setShouldRecenter(true);
      setMapCenter(null); // Clear manual center to allow auto-zoom
      setTimeout(() => setShouldRecenter(false), 100);
    } else if (patientLat && patientLng) {
      // Only patient location available, center on patient
      setMapCenter([patientLat, patientLng]);
      setShouldRecenter(true);
      setTimeout(() => setShouldRecenter(false), 100);
    }
  }, [patientLocation, driverLocation]);

  // Contact driver
  const contactDriver = useCallback(() => {
    if (driverInfo && driverInfo.phone) {
      window.location.href = `tel:${driverInfo.phone}`;
    }
  }, [driverInfo]);

  // Auto-start location sharing when component mounts with a request
  useEffect(() => {
    console.log('Patient auto-start check:', { 
      requestId, 
      isLocationSharing, 
      locationPermission,
      driverLocation: !!driverLocation 
    });
    
    if (requestId && !isLocationSharing && locationPermission !== 'denied') {
      // Auto-start location sharing for emergency requests
      console.log('Auto-starting location sharing for patient in 3 seconds...');
      
      const timeoutId = setTimeout(() => {
        console.log('Executing auto-start location sharing for patient');
        startLocationSharing();
      }, 3000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [requestId, isLocationSharing, locationPermission, startLocationSharing]);

  // Setup WebSocket event listeners
  useEffect(() => {
    if (!requestId) return;

    const locationReceivedHandler = (data) => {
      console.log('Patient received location event:', data);
      if (data.requestId === requestId && data.userType === 'driver') {
        console.log('Processing driver location update for patient:', data.location);
        // Driver location update received - this should update the driverLocation via parent component
        setDistanceInfo(data.distanceInfo);
      }
    };

    const locationUpdateSuccessHandler = (data) => {
      if (data.requestId === requestId) {
        setDistanceInfo(data.distanceInfo);
      }
    };

    const distanceInfoHandler = (data) => {
      if (data.requestId === requestId) {
        setDistanceInfo(data);
      }
    };

    const trackingActiveHandler = (data) => {
      if (data.requestId === requestId) {
        console.log('Location tracking is now active');
        requestDistanceInfo();
      }
    };

    // Subscribe to events
    websocketService.subscribe('location:received', locationReceivedHandler);
    websocketService.subscribe('location:update-success', locationUpdateSuccessHandler);
    websocketService.subscribe('location:distance-info', distanceInfoHandler);
    websocketService.subscribe('location:tracking-active', trackingActiveHandler);

    return () => {
      websocketService.unsubscribe('location:received', locationReceivedHandler);
      websocketService.unsubscribe('location:update-success', locationUpdateSuccessHandler);
      websocketService.unsubscribe('location:distance-info', distanceInfoHandler);
      websocketService.unsubscribe('location:tracking-active', trackingActiveHandler);
    };
  }, [requestId, requestDistanceInfo]);

  // Update estimated arrival when distance info changes
  useEffect(() => {
    calculateEstimatedArrival();
  }, [calculateEstimatedArrival]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (locationSubscription) {
        geolocationService.stopWatching(locationSubscription);
      }
    };
  }, [locationSubscription]);

  // Prepare markers for map
  const markers = [];
  
  console.log('PatientTrackingMap - Raw patientLocation:', patientLocation);
  console.log('PatientTrackingMap - Raw driverLocation:', driverLocation);
  console.log('PatientTrackingMap - Raw initialPatientLocation:', initialPatientLocation);
  
  // Normalize patient location data - use current patientLocation first, then fallback to initialPatientLocation
  const normalizedPatientLocation = (() => {
    // First try to use current patientLocation (from location sharing)
    if (patientLocation) {
      const lat = patientLocation.latitude || patientLocation.lat;
      const lng = patientLocation.longitude || patientLocation.lng;
      if (lat != null && lng != null && !isNaN(lat) && !isNaN(lng)) {
        return {
          latitude: lat,
          longitude: lng,
          accuracy: patientLocation.accuracy
        };
      }
    }
    
    // Fallback to initialPatientLocation if current location is not available
    if (initialPatientLocation) {
      const lat = initialPatientLocation.latitude || initialPatientLocation.lat;
      const lng = initialPatientLocation.longitude || initialPatientLocation.lng;
      if (lat != null && lng != null && !isNaN(lat) && !isNaN(lng)) {
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'PatientTrackingMap.js:280',message:'Using initialPatientLocation for patient marker',data:{lat,lng,hasPatientLocation:!!patientLocation,initialLocationSource:'initialPatientLocation'},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
        // #endregion
        return {
          latitude: lat,
          longitude: lng,
          accuracy: initialPatientLocation.accuracy
        };
      }
    }
    
    return null;
  })();
  
  // Normalize driver location data - only if valid coordinates exist
  const normalizedDriverLocation = driverLocation ? (() => {
    const lat = driverLocation.latitude || driverLocation.lat;
    const lng = driverLocation.longitude || driverLocation.lng;
    // Only create normalized object if we have valid coordinates
    if (lat != null && lng != null && !isNaN(lat) && !isNaN(lng)) {
      return {
        latitude: lat,
        longitude: lng,
        accuracy: driverLocation.accuracy,
        speed: driverLocation.speed
      };
    }
    return null;
  })() : null;
  
  console.log('PatientTrackingMap - Normalized patientLocation:', normalizedPatientLocation);
  console.log('PatientTrackingMap - Normalized driverLocation:', normalizedDriverLocation);
  
  if (normalizedPatientLocation && normalizedPatientLocation.latitude && normalizedPatientLocation.longitude) {
    console.log('PatientTrackingMap - Adding patient marker:', normalizedPatientLocation);
    markers.push({
      id: 'patient',
      position: {
        lat: normalizedPatientLocation.latitude,
        lng: normalizedPatientLocation.longitude
      },
      type: MARKER_TYPES.PATIENT,
      color: MARKER_COLORS.PATIENT,
      popup: (
        <div>
          <strong>Your Location</strong>
          <br />
          <small>
            Accuracy: ±{Math.round(normalizedPatientLocation.accuracy || 0)}m
          </small>
        </div>
      )
    });
  } else {
    console.log('PatientTrackingMap - Patient location missing or incomplete:', patientLocation);
  }

  if (normalizedDriverLocation && normalizedDriverLocation.latitude && normalizedDriverLocation.longitude) {
    console.log('PatientTrackingMap - Adding driver marker:', normalizedDriverLocation);
    markers.push({
      id: 'driver',
      position: {
        lat: normalizedDriverLocation.latitude,
        lng: normalizedDriverLocation.longitude
      },
      type: MARKER_TYPES.AMBULANCE,
      color: MARKER_COLORS.AMBULANCE,
      popup: (
        <div>
          <strong>Ambulance Location</strong>
          <br />
          <small>
            Driver: {driverInfo?.name || 'Unknown'}
            <br />
            Vehicle: {driverInfo?.vehicleNumber || 'N/A'}
            {normalizedDriverLocation.speed && (
              <><br />Speed: {Math.round(normalizedDriverLocation.speed * 3.6)} km/h</>
            )}
          </small>
        </div>
      )
    });
  } else {
    console.log('PatientTrackingMap - Driver location missing or incomplete:', driverLocation);
  }

  const currentMapCenter = mapCenter || (autoCenter && normalizedPatientLocation 
    ? [normalizedPatientLocation.latitude, normalizedPatientLocation.longitude]
    : [40.7128, -74.0060]);

  return (
    <div className={`patient-tracking-map ${className}`}>
      {/* Control Panel */}
      {showControls && (
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900">Ambulance Tracking</h3>
            {driverInfo && (
              <button
                onClick={contactDriver}
                className="bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700 text-sm"
              >
                <PhoneIcon className="h-4 w-4 inline mr-1" />
                Call Driver
              </button>
            )}
          </div>

          {/* Driver Info */}
          {driverInfo && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-3">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
                <div className="flex-1 w-full sm:w-auto">
                  <p className="font-semibold text-base sm:text-lg text-blue-900 mb-2">{driverInfo.name}</p>
                  <div className="space-y-1.5 sm:space-y-2">
                    <p className="text-sm sm:text-base text-blue-700">
                      <strong className="font-semibold">Mobile:</strong> {driverInfo.phone || 'N/A'}
                    </p>
                    <p className="text-sm sm:text-base text-blue-700">
                      <strong className="font-semibold">Ambulance Type:</strong> {driverInfo.ambulanceType || driverInfo.vehicleType || 'N/A'}
                    </p>
                    {driverInfo.vehicleNumber && (
                      <p className="text-sm sm:text-base text-blue-700">
                        <strong className="font-semibold">Vehicle Number:</strong> {driverInfo.vehicleNumber}
                      </p>
                    )}
                  </div>
                </div>
                {distanceInfo && (
                  <div className="text-left sm:text-right w-full sm:w-auto flex-shrink-0">
                    <div className="text-xl sm:text-2xl font-bold text-blue-900 mb-1">
                      {distanceInfo.eta} min
                    </div>
                    <div className="text-sm sm:text-base text-blue-700 font-medium">
                      {distanceInfo.distance} km away
                    </div>
                    {estimatedArrival && (
                      <div className="text-xs sm:text-sm text-blue-600 mt-1.5 font-medium">
                        ETA: {estimatedArrival.toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className={`border rounded-md p-3 mb-3 ${
              error.includes('Timeout') || error.includes('timeout') || error.includes('expired') 
                ? 'bg-yellow-50 border-yellow-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start justify-between">
                <div className="flex">
                  <ExclamationTriangleIcon className={`h-5 w-5 mr-2 ${
                    error.includes('Timeout') || error.includes('timeout') || error.includes('expired')
                      ? 'text-yellow-400' 
                      : 'text-red-400'
                  }`} />
                  <div>
                    <p className={`text-sm font-medium ${
                      error.includes('Timeout') || error.includes('timeout') || error.includes('expired')
                        ? 'text-yellow-700' 
                        : 'text-red-700'
                    }`}>
                      {error.includes('Timeout') || error.includes('timeout') || error.includes('expired') 
                        ? 'Location Taking Longer Than Expected'
                        : error}
                    </p>
                    {(error.includes('Timeout') || error.includes('timeout') || error.includes('expired')) && (
                      <p className="text-xs text-yellow-600 mt-1">
                        Make sure you're outdoors or near a window. GPS can take 10-15 seconds.
                      </p>
                    )}
                  </div>
                </div>
                {(error.includes('Timeout') || error.includes('timeout') || error.includes('expired')) && !isLocationSharing && (
                  <button
                    onClick={startLocationSharing}
                    disabled={isLoading}
                    className="ml-2 text-xs bg-yellow-600 text-white px-2 py-1 rounded hover:bg-yellow-700 disabled:opacity-50 whitespace-nowrap"
                  >
                    Try Again
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-2">
            {!isLocationSharing ? (
              <button
                onClick={startLocationSharing}
                disabled={isLoading}
                className="bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                    Requesting...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <MapPinIcon className="h-3 w-3 mr-1" />
                    Share Location
                  </span>
                )}
              </button>
            ) : (
              <button
                onClick={stopLocationSharing}
                className="bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700 text-sm"
              >
                <span className="flex items-center">
                  <MapPinIcon className="h-3 w-3 mr-1" />
                  Stop Sharing
                </span>
              </button>
            )}

            {patientLocation && (
              <button
                onClick={recenterMap}
                className="bg-gray-600 text-white px-3 py-2 rounded-md hover:bg-gray-700 text-sm recenter-button"
                title={driverLocation ? "Show both patient and ambulance locations" : "Center map on your location"}
              >
                <ArrowsPointingOutIcon className="h-3 w-3 recenter-button-icon" />
              </button>
            )}

            <button
              onClick={requestDistanceInfo}
              className="bg-gray-600 text-white px-3 py-2 rounded-md hover:bg-gray-700 text-sm"
              title="Refresh distance info"
            >
              <ClockIcon className="h-3 w-3" />
            </button>
          </div>

          {/* Status */}
          <div className="mt-3 text-sm text-gray-600">
            Status: {isLocationSharing ? (
              <span className="text-green-600 font-medium">Sharing location</span>
            ) : (
              <span className="text-gray-500">Not sharing</span>
            )}
            {locationPermission === 'denied' && (
              <span className="text-red-600 ml-2">• Permission denied</span>
            )}
          </div>

          {/* Instructions */}
          {!isLocationSharing && (
            <div className="mt-3 text-xs text-gray-500">
              Share your location to help the driver find you quickly
            </div>
          )}
        </div>
      )}

      {/* Map */}
      <BaseMap
        center={currentMapCenter}
        zoom={15}
        markers={markers}
        height={height}
        autoZoom={markers.length > 1 && !shouldRecenter}
        className="rounded-lg shadow-sm border"
        key={shouldRecenter ? Date.now() : 'map'}
      />

      {/* Map Info */}
      {markers.length === 0 && (
        <div className="mt-4 text-center text-gray-500 text-sm">
          Waiting for location data. Share your location to see the map.
        </div>
      )}
    </div>
  );
};

export default PatientTrackingMap;
